'use client';

import { c as _c } from "react/compiler-runtime";
import { type HttpRequestEvent, type QueryClient, type QueryKey, subscribeHttpRequests, useQueryClient } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef, useState } from 'react';
import { liveEventDiagnosticContext, safeLiveEventError } from '@/lib/live-events/diagnostic-context';
import { type DiagnosticSchedule, diagnosticScheduleSchema, populatedCatalogSchema } from '@/lib/live-events/diagnostic-validation';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { containDiagnosticFailure } from '@/lib/telemetry/live-event-diagnostics';
import { logger } from '@/lib/telemetry/logger';
export interface DiagnosticScope {
  kind: 'schedule' | 'event_catalog' | 'proposal_catalog' | 'market_catalog';
  action: 'create_proposal' | 'back_market' | 'trade_market' | 'event_detail' | 'event_feed' | 'proposal_catalog';
  eventId?: string | undefined;
  batchId?: string;
  batchIndex?: number;
  hasDisplayedResults?: () => boolean;
  marketStates?: readonly {
    pubkey: string;
    status: string;
  }[];
}
interface ScopeFields {
  action: DiagnosticScope['action'];
  eventId: string | undefined;
  batchId: string | undefined;
  batchIndex: number | undefined;
}
type RequestOwnerFields = {
  ownership: 'unregistered';
} | (ScopeFields & {
  ownership: 'active';
  actions: DiagnosticScope['action'][];
  partialResults: boolean;
});
interface Registration {
  queryKey: QueryKey;
  owners: Set<() => DiagnosticScope>;
  lastScheduleIssue?: string;
  marketIssues: Map<string, string>;
}
interface OwnerSnapshot {
  context: DiagnosticScope;
  network: string;
}
interface ActiveQueries {
  client: QueryClient;
  network: string;
  scopes: Scopes;
}
type Scopes = Map<string, Registration>;
type QueryCache = ReturnType<QueryClient['getQueryCache']>;
type CacheEvent = Parameters<Parameters<QueryCache['subscribe']>[0]>[0];
const registrations: WeakMap<QueryClient, Map<string, Scopes>> = new WeakMap();
const observedPages: WeakSet<Promise<void>> = new WeakSet();
const activeQueries: Set<ActiveQueries> = new Set();
let httpReporterInstalled = false;
const requestKinds: ReadonlyMap<string, DiagnosticScope['kind']> = new Map([['useGetEvent', 'schedule'], ['useGetEvents', 'event_catalog'], ['useGetMarkets', 'market_catalog'], ['useGetProposals', 'proposal_catalog']]);
const marketStatuses: ReadonlySet<string> = new Set(['created', 'backing', 'baking', 'trading', 'resolved', 'graduated']);

/** One owner under SDKProvider: shared observers never multiply failures. */
export function LiveEventQueryDiagnostics() {
  const $ = _c(4);
  const client = useQueryClient();
  const network = useActiveNetwork();
  let t0;
  let t1;
  if ($[0] !== client || $[1] !== network) {
    t0 = () => {
      const scopes = scopesFor(client, network);
      const active = {
        client,
        network,
        scopes
      };
      activeQueries.add(active);
      installHttpReporter();
      const offCache = client.getQueryCache().subscribe(event => containDiagnosticFailure(() => observeQuery(event, scopes, network), {
        stage: "cache_observation",
        network
      }));
      return () => {
        activeQueries.delete(active);
        offCache();
      };
    };
    t1 = [client, network];
    $[0] = client;
    $[1] = network;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
}
export function useLiveEventQueryDiagnostics(queryKey, context) {
  const $ = _c(17);
  const client = useQueryClient();
  const network = useActiveNetwork();
  let t0;
  if ($[0] !== queryKey) {
    t0 = JSON.stringify(queryKey);
    $[0] = queryKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const key = t0;
  let t1;
  if ($[2] !== context || $[3] !== network) {
    t1 = {
      context,
      network
    };
    $[2] = context;
    $[3] = network;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const current = useRef(t1);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => request => {
      observePageRequest(request, current.current);
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [observePage] = useState(t2);
  let t3;
  let t4;
  if ($[6] !== client || $[7] !== context || $[8] !== key || $[9] !== network) {
    t3 = () => {
      current.current = {
        context,
        network
      };
      const scope = scopesFor(client, network).get(key);
      if (scope !== undefined) {
        containDiagnosticFailure(() => inspectCachedScope(client, scope, network), {
          stage: "cached_observation",
          network
        });
      }
    };
    t4 = [client, context, key, network];
    $[6] = client;
    $[7] = context;
    $[8] = key;
    $[9] = network;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[12] !== client || $[13] !== key || $[14] !== network) {
    t5 = () => {
      const scopes = scopesFor(client, network);
      const scope_0 = scopes.get(key) ?? {
        queryKey: JSON.parse(key) as QueryKey,
        owners: new Set(),
        marketIssues: new Map()
      };
      const owner = () => current.current.context;
      scope_0.owners.add(owner);
      scopes.set(key, scope_0);
      containDiagnosticFailure(() => inspectCachedScope(client, scope_0, network), {
        stage: "cached_observation",
        network
      });
      return () => {
        scope_0.owners.delete(owner);
        if (scope_0.owners.size === 0) {
          scopes.delete(key);
        }
      };
    };
    t6 = [client, key, network];
    $[12] = client;
    $[13] = key;
    $[14] = network;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  return observePage;
}
function installHttpReporter(): void {
  if (httpReporterInstalled) return;
  httpReporterInstalled = true;
  // HTTP completion outlives screens. Keep one app-lifetime listener; active
  // cache/owner records are removed on unmount, never retained by this callback.
  subscribeHttpRequests(event => containDiagnosticFailure(() => reportRequest(event), {
    stage: 'http_attempt',
    network: event.network ?? 'unknown',
    attemptId: event.correlationId,
    correlationId: event.correlationId
  }));
}
function observePageRequest(request: Promise<void>, owner: OwnerSnapshot): void {
  if (observedPages.has(request)) return;
  observedPages.add(request);
  const {
    context,
    network
  } = owner;
  // Capture the owner at invocation: a network switch or remount must not
  // relabel an already-running page request with a newer screen's context.
  void request.catch((error: unknown) => containDiagnosticFailure(() => {
    logger.warn('live_events.query_stopped', {
      ...liveEventDiagnosticContext(),
      ...scopeFields(context),
      ...safeLiveEventError(error),
      network,
      stage: 'next_page_settlement',
      outcome: 'terminal',
      partialResults: context.hasDisplayedResults?.() ?? false
    });
  }, {
    stage: 'next_page_settlement',
    network
  }));
}
function reportRequest(event: HttpRequestEvent): void {
  if (event.outcome !== 'http_error' && event.outcome !== 'network_error') return;
  if (event.liveEvent === undefined) return;
  const kind = requestKinds.get(event.operationName);
  if (kind === undefined) return;
  logger.warn(messageFor(kind), {
    ...liveEventDiagnosticContext(),
    eventId: safeIdentifier(event.liveEvent.eventId),
    batchId: safeIdentifier(event.liveEvent.batchId),
    ...requestOwnership(event),
    network: event.network ?? 'unknown',
    stage: kind === 'schedule' ? 'schedule_lookup' : 'catalog_lookup',
    reason: 'lookup_attempt_failed',
    outcome: event.outcome,
    attemptId: event.correlationId,
    correlationId: event.correlationId,
    attempt: event.retryCount + 1,
    durationMs: event.durationMs,
    operation: event.operationName,
    page: event.liveEvent.page,
    requestId: event.requestId,
    pageId: event.liveEvent.pageId,
    httpStatus: event.statusCode
  });
}
function requestOwnership(event: HttpRequestEvent): RequestOwnerFields {
  for (const active of activeQueries) {
    if (event.network !== undefined && event.network !== active.network) continue;
    const matching = [...active.scopes.values()].filter(scope => contexts(scope).some(context => matchesRequest(event, context)));
    if (matching.length > 0) return registeredRequestFields(matching, active.client);
  }
  return {
    ownership: 'unregistered'
  };
}
function registeredRequestFields(matching: readonly Registration[], client: QueryClient): RequestOwnerFields {
  const owners = matching.flatMap(contexts);
  const context = owners[0];
  const scope = matching[0];
  if (context === undefined || scope === undefined) return {
    ownership: 'unregistered'
  };
  const query = client.getQueryCache().find({
    queryKey: scope.queryKey,
    exact: true
  });
  return {
    ...scopeFields(context),
    ownership: 'active',
    actions: [...new Set(owners.map(owner => owner.action))],
    partialResults: owners.some(owner => owner.hasDisplayedResults?.() ?? populatedCatalogSchema.safeParse(query?.state.data).success)
  };
}
function matchesRequest(event: HttpRequestEvent, scope: DiagnosticScope): boolean {
  if (event.liveEvent === undefined) return false;
  if (scope.kind === 'schedule') return event.operationName === 'useGetEvent' && event.liveEvent.eventId === scope.eventId;
  if (scope.kind === 'event_catalog') {
    return event.operationName === 'useGetEvents';
  }
  if (scope.kind === 'market_catalog') return event.operationName === 'useGetMarkets' && event.liveEvent.eventId === scope.eventId;
  return event.operationName === 'useGetProposals' && event.liveEvent.batchId === scope.batchId;
}
function observeQuery(event: CacheEvent, scopes: Scopes, network: string): void {
  if (event.type !== 'updated') return;
  const scope = scopes.get(JSON.stringify(event.query.queryKey));
  const context = scope === undefined ? undefined : contexts(scope)[0];
  if (context !== undefined && event.action.type === 'error') {
    logger.warn('live_events.query_stopped', {
      ...liveEventDiagnosticContext(),
      ...scopeFields(context),
      ...safeLiveEventError(event.action.error),
      network,
      stage: 'query_settlement',
      outcome: 'terminal',
      queryFailureCount: event.query.state.fetchFailureCount
    });
  }
  if (event.action.type !== 'success') return;
  if (scope !== undefined && !(event.action.manual === true && event.query.state.fetchStatus === 'fetching')) {
    reportScheduleIssue(scope, diagnosticScheduleSchema.parse(event.query.state.data), network, event.action.manual !== true);
  }
}
function inspectCachedScope(client: QueryClient, scope: Registration, network: string): void {
  const query = client.getQueryCache().find({
    queryKey: scope.queryKey,
    exact: true
  });
  if (query?.state.data !== undefined) reportScheduleIssue(scope, diagnosticScheduleSchema.parse(query.state.data), network);
  reportMarketIssues(scope, network);
}
function reportScheduleIssue(scope: Registration, schedule: DiagnosticSchedule, network: string, freshFetch: boolean = false): void {
  const owners = contexts(scope);
  const context = owners[0];
  if (context?.kind !== 'schedule' || context.eventId === undefined) return;
  const reason = scheduleReason(schedule, context.eventId);
  if (reason === undefined) {
    delete scope.lastScheduleIssue;
    return;
  }
  const observation = JSON.stringify([reason, context.eventId, schedule.eventId.fingerprint, schedule.tradingStartAt.fingerprint, schedule.tradingEndAt.fingerprint]);
  if (!freshFetch && scope.lastScheduleIssue === observation) return;
  logger.warn('live_events.schedule_unavailable', {
    ...liveEventDiagnosticContext(),
    ...scopeFields(context),
    network,
    actions: [...new Set(owners.map(owner => owner.action))],
    stage: 'schedule_validation',
    reason,
    outcome: 'terminal',
    observationId: crypto.randomUUID()
  });
  scope.lastScheduleIssue = observation;
}
function scheduleReason(schedule: DiagnosticSchedule, eventId: string): 'identity_mismatch' | 'invalid_schedule' | undefined {
  if (schedule.eventId.value !== eventId) return 'identity_mismatch';
  if (schedule.tradingStartAt.value === undefined || schedule.tradingEndAt.value === undefined) return 'invalid_schedule';
  const start = Date.parse(schedule.tradingStartAt.value);
  const end = Date.parse(schedule.tradingEndAt.value);
  return Number.isFinite(start) && Number.isFinite(end) && end > start ? undefined : 'invalid_schedule';
}
function reportMarketIssues(scope: Registration, network: string): void {
  const context = contexts(scope)[0];
  if (context?.kind !== 'market_catalog' || context.marketStates === undefined) return;
  const present = new Set(context.marketStates.map(market => market.pubkey));
  for (const market of context.marketStates) reportMarketIssue(scope, context, market, network);
  for (const pubkey of scope.marketIssues.keys()) if (!present.has(pubkey)) scope.marketIssues.delete(pubkey);
}
function reportMarketIssue(scope: Registration, context: DiagnosticScope, market: {
  pubkey: string;
  status: string;
}, network: string): void {
  if (marketStatuses.has(market.status)) {
    scope.marketIssues.delete(market.pubkey);
    return;
  }
  if (scope.marketIssues.get(market.pubkey) === market.status) return;
  logger.warn('live_events.market_status_unrecognized', {
    ...liveEventDiagnosticContext(),
    ...scopeFields(context),
    network,
    marketId: safeIdentifier(market.pubkey),
    status: /^[a-z_]{1,32}$/.test(market.status) ? market.status : 'unrecognized',
    stage: 'market_mapping',
    outcome: 'terminal',
    observationId: crypto.randomUUID()
  });
  scope.marketIssues.set(market.pubkey, market.status);
}
function scopesFor(client: QueryClient, network: string): Scopes {
  let networks = registrations.get(client);
  if (networks === undefined) {
    networks = new Map();
    registrations.set(client, networks);
  }
  let scopes = networks.get(network);
  if (scopes === undefined) {
    scopes = new Map();
    networks.set(network, scopes);
  }
  return scopes;
}
function contexts(scope: Registration): DiagnosticScope[] {
  return [...scope.owners].map(read => read());
}
function scopeFields(scope: DiagnosticScope): ScopeFields {
  return {
    action: scope.action,
    eventId: safeIdentifier(scope.eventId),
    batchId: safeIdentifier(scope.batchId),
    batchIndex: scope.batchIndex
  };
}
function safeIdentifier(value: string | undefined): string | undefined {
  return value !== undefined && /^[A-Za-z0-9_-]{1,64}$/.test(value) ? value : undefined;
}
function messageFor(kind: DiagnosticScope['kind']): string {
  if (kind === 'schedule') return 'live_events.schedule_unavailable';
  if (kind === 'event_catalog') return 'live_events.event_catalog_incomplete';
  return 'live_events.proposal_catalog_incomplete';
}